import { useEffect, useState } from 'react'
import { useSignTypedData, useAccount } from 'wagmi'
import { CARD_PARTNER_ID, CARD_AUTH_VERSION } from '@/config/consts'
import { toAbsoluteExpiryMs } from '@/features/card/utils/toAbsoluteExpiryMs'
import { withTrackingId } from '@/utils/trackedFetch'
import { CardAuthRequest, CardAuthTokens } from '../types/CardState'
import { useCardAuth as useCardAuthStore } from '@/domain/state'
import { setCardTrackingWallet } from './useAuthenticatedMutation'

interface UseCardAuthResult {
  tokens: CardAuthTokens | null
  login: () => Promise<CardAuthTokens>
  isLoading: boolean
  error: string | null
  hasValidTokens: () => boolean // Function to check current state, avoiding stale closures
  hasTokens: boolean // Check if any tokens exist (regardless of validity)
  clearTokens: (options?: { clearServerSession?: boolean }) => Promise<void>
}

// EIP-712 type definitions for card authentication
export const CARD_AUTH_TYPES = {
  EIP712Domain: [
    { name: 'name', type: 'string' },
    { name: 'version', type: 'string' },
  ],
  Login: [
    { name: 'timestamp', type: 'uint256' },
    { name: 'tenantID', type: 'string' },
  ],
} as const

export function useCardAuth(): UseCardAuthResult {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { address } = useAccount()
  const { signTypedData } = useSignTypedData()
  const cardAuthStore = useCardAuthStore()

  // Convert store state to tokens format
  const tokens: CardAuthTokens | null = cardAuthStore.hasValidTokens()
    ? {
        accessToken: cardAuthStore.accessToken!,
        expiresIn: cardAuthStore.expiresIn!,
        tokenType: cardAuthStore.tokenType!,
      }
    : null

  // Sync active address in the per-address store. Switching wallets just
  // changes which slot is active — no tokens are cleared.
  // biome-ignore lint/correctness/useExhaustiveDependencies: cardAuthStore methods are stable via Zustand
  useEffect(() => {
    setCardTrackingWallet(address)
    cardAuthStore.setActiveAddress(address)
  }, [address])

  const login = async (): Promise<CardAuthTokens> => {
    if (!address) {
      throw new Error('Wallet not connected')
    }

    setIsLoading(true)
    setError(null)

    // Ensure activeAddress is set before any token operations
    cardAuthStore.setActiveAddress(address)

    try {
      const timestamp = Math.floor(Date.now() / 1000)

      const typedData = {
        types: CARD_AUTH_TYPES,
        primaryType: 'Login' as const,
        domain: {
          name: 'HypurrFiCards',
          version: CARD_AUTH_VERSION,
        },
        message: {
          timestamp: BigInt(timestamp),
          tenantID: CARD_PARTNER_ID,
        },
      }

      // Sign the typed data
      const signature = await new Promise<string>((resolve, reject) => {
        signTypedData(typedData, {
          onSuccess: (signature) => resolve(signature),
          onError: (error) => reject(error),
        })
      })

      // Prepare the request body
      const requestBody: CardAuthRequest = {
        eoa: address,
        sig: signature,
        timestamp,
        version: CARD_AUTH_VERSION,
      }

      const response = await fetch(
        '/api/card/auth/session',
        withTrackingId(
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          },
          address,
        ),
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.data?.error || errorData.error || `Authentication failed with status ${response.status}`,
        )
      }

      const authResponse = await response.json()
      const authTokens = authResponse.data

      const enhancedTokens: CardAuthTokens = {
        ...authTokens,
        expiresIn: toAbsoluteExpiryMs(authTokens.expiresIn),
      }

      cardAuthStore.setTokens(enhancedTokens)

      return enhancedTokens
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message.includes('User rejected')
            ? 'User rejected the signature request'
            : error.message
          : 'Authentication failed'

      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const clearTokens = async ({ clearServerSession = true }: { clearServerSession?: boolean } = {}): Promise<void> => {
    const currentAddress = cardAuthStore.activeAddress ?? address
    cardAuthStore.clearTokens()

    if (!clearServerSession || !currentAddress) return

    setError(null)

    try {
      const response = await fetch(
        '/api/card/auth/session',
        withTrackingId(
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              address: currentAddress,
            }),
          },
          currentAddress,
        ),
      )

      if (response.ok) return

      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.data?.error ||
          errorData.error ||
          errorData.message ||
          `Failed to clear card auth session: ${response.status} ${response.statusText}`,
      )
    } catch (clearError) {
      const errorMessage = clearError instanceof Error ? clearError.message : 'Failed to clear card auth session cookie'
      console.error('Failed to clear card auth session cookie:', clearError)
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  return {
    tokens,
    login,
    isLoading,
    error,
    // Return function to check current state, avoiding stale closure issues
    hasValidTokens: () => cardAuthStore.hasValidTokens(),
    hasTokens: !!(cardAuthStore.accessToken || cardAuthStore.hasRefreshSession),
    clearTokens,
  }
}
